import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, TextField, IconButton, List, ListItem, ListItemAvatar,
  ListItemText, Avatar, Badge, Divider, CircularProgress, Alert, Chip, InputAdornment,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, Menu, MenuItem,
  Tooltip, Popover, LinearProgress,
} from '@mui/material';
import {
  Send, Search, Add, Chat as ChatIcon, DoneAll, Done, Reply, Close,
  EmojiEmotions, MoreVert, PushPin, VolumeOff, Delete, Edit, InsertDriveFile,
  Image as ImageIcon, AttachFile, Videocam, Call, Mic, AudioFile, Download,
  Circle,
} from '@mui/icons-material';
import { chatAPI } from '../../api/client';
import useAuthStore from '../../store/useStore';
import { format, isToday, isYesterday, formatDistanceToNow } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8000';
const roleColors = {
  super_admin: '#d32f2f', landlord: '#1976d2', agent: '#388e3c',
  tenant: '#f57c00', rent_control: '#7b1fa2',
};
const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];
const EMOJI_PICKER = [
  '😀','�','😄','😁','😆','😅','🤣','�','🙂','🙃','😉','😊','😇','🥰','😍',
  '🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫',
  '🤔','🫡','🤐','🤨','😐','😑','😶','🫥','😏','😒','🙄','😬','🤥','😌','😔',
  '😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🥵','🥶','🥴','😵','🤯','🤠','🥳',
  '🥸','�','🤓','🧐','😕','🫤','😟','🙁','�','😯','😲','😳','🥺','🥹','😦',
  '�','😨','😰','😥','�','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤',
  '😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👻','👽','👾','🤖','😺',
  '👋','🤚','🖐️','✋','🖖','🫱','🫲','🫳','🫴','👌','🤌','🤏','✌️','🤞','🫰',
  '🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','🫵','👍','👎','✊','👊','🤛',
  '🤜','�','🙌','🫶','👐','🤲','🤝','��','❤️','🧡','💛','💚','💙','💜','🖤',
  '🤍','🤎','💔','❤️‍🔥','❤️‍🩹','❣️','💕','💞','💓','💗','💖','💘','💝','💟','🔥',
  '⭐','🌟','✨','💫','🎉','🎊','🎈','🎁','🏆','🥇','🥈','🥉','⚽','🏀','🎵',
];

function formatMsgDate(dateStr) {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday ' + format(d, 'HH:mm');
  return format(d, 'dd/MM/yy HH:mm');
}
function formatConvoTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, 'HH:mm');
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'dd/MM/yy');
}

function ChatPage() {
  const { user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [msgLoading, setMsgLoading] = useState(false);
  const [error, setError] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [chattableUsers, setChattableUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [editingMsg, setEditingMsg] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [contextMsg, setContextMsg] = useState(null);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const [emojiMsg, setEmojiMsg] = useState(null);
  const [emojiInputAnchor, setEmojiInputAnchor] = useState(null);
  const [typingUsers, setTypingUsers] = useState([]);
  const [convoMenuAnchor, setConvoMenuAnchor] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [attachMenuAnchor, setAttachMenuAnchor] = useState(null);
  const messagesEndRef = useRef(null);
  const typingTimerRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const fetchConversations = useCallback(async (isPolling = false) => {
    try {
      const res = await chatAPI.getConversations();
      const data = Array.isArray(res.data) ? res.data : (res.data?.conversations || []);
      setConversations(data);
      if (!isPolling) setError('');
    } catch (err) {
      if (!isPolling) {
        console.warn('Failed to load conversations:', err?.response?.status);
        // Only show error if it's not a 401 (handled by auth) and not polling
        if (err?.response?.status !== 401) setError('Failed to load conversations');
      }
    } finally { setLoading(false); }
  }, []);

  const fetchMessages = useCallback(async (convoId, isPolling = false) => {
    try {
      if (!isPolling) setMsgLoading(true);
      const res = await chatAPI.getMessages(convoId, { per_page: 100 });
      const data = res.data;
      const msgs = Array.isArray(data) ? data : (data?.messages || []);
      setMessages(msgs);
    } catch (err) {
      if (!isPolling) {
        console.warn('Failed to load messages:', err?.response?.status);
        if (err?.response?.status !== 401) setError('Failed to load messages');
      }
    } finally { if (!isPolling) setMsgLoading(false); }
  }, []);

  useEffect(() => {
    fetchConversations(false);
    const id = setInterval(() => fetchConversations(true), 4000);
    return () => clearInterval(id);
  }, [fetchConversations]);

  // Deep link: open conversation from notification ?conversation=<id>
  useEffect(() => {
    const convoParam = searchParams.get('conversation');
    if (convoParam && conversations.length > 0 && !selectedConvo) {
      const found = conversations.find(c => String(c.id) === convoParam);
      if (found) setSelectedConvo(found);
    }
  }, [searchParams, conversations, selectedConvo]);

  useEffect(() => {
    if (selectedConvo) {
      fetchMessages(selectedConvo.id, false);
      const id = setInterval(() => fetchMessages(selectedConvo.id, true), 2500);
      return () => clearInterval(id);
    }
  }, [selectedConvo?.id, fetchMessages]);

  useEffect(() => {
    if (!selectedConvo) return;
    const id = setInterval(async () => {
      try {
        const res = await chatAPI.getTyping(selectedConvo.id);
        setTypingUsers(res.data.typing_users || []);
      } catch (_) {}
    }, 2000);
    return () => clearInterval(id);
  }, [selectedConvo?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTyping = () => {
    if (!selectedConvo) return;
    chatAPI.setTyping({ conversation_id: selectedConvo.id, is_typing: true }).catch(() => {});
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      chatAPI.setTyping({ conversation_id: selectedConvo.id, is_typing: false }).catch(() => {});
    }, 3000);
  };

  const handleFileUpload = async (e, forceType) => {
    const file = e.target.files?.[0];
    if (!file || !selectedConvo) return;
    setUploading(true); setAttachMenuAnchor(null);
    try {
      const res = await chatAPI.uploadFile(file);
      const msgType = forceType || res.data.message_type || 'file';
      await chatAPI.sendMessage(selectedConvo.id, {
        content: msgType === 'image' ? '' : file.name,
        message_type: msgType,
        attachment_url: res.data.url,
        attachment_name: res.data.filename,
        attachment_size: res.data.size,
        attachment_mime: res.data.mime_type,
      });
      fetchMessages(selectedConvo.id);
      fetchConversations();
    } catch (err) { setError(err.response?.data?.detail || 'File upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConvo) return;
    try {
      if (editingMsg) {
        await chatAPI.editMessage(editingMsg.id, { content: newMessage.trim() });
        setEditingMsg(null);
      } else {
        const payload = { content: newMessage.trim(), message_type: 'text' };
        if (replyTo) payload.reply_to_id = replyTo.id;
        await chatAPI.sendMessage(selectedConvo.id, payload);
        setReplyTo(null);
      }
      setNewMessage('');
      chatAPI.setTyping({ conversation_id: selectedConvo.id, is_typing: false }).catch(() => {});
      fetchMessages(selectedConvo.id);
      fetchConversations();
    } catch (err) { setError('Failed to send message'); }
  };

  const handleDeleteMsg = async (msgId) => {
    try {
      await chatAPI.deleteMessage(msgId);
      fetchMessages(selectedConvo.id);
    } catch (err) { setError('Failed to delete'); }
    setContextMenu(null);
  };

  const handleReact = async (msgId, emoji) => {
    try {
      await chatAPI.reactToMessage(msgId, { emoji });
      fetchMessages(selectedConvo.id);
    } catch (_) {}
    setEmojiAnchor(null);
    setEmojiMsg(null);
  };

  const handlePinConvo = async () => {
    if (!selectedConvo) return;
    try { await chatAPI.pinConversation(selectedConvo.id); fetchConversations(); } catch (_) {}
    setConvoMenuAnchor(null);
  };
  const handleMuteConvo = async () => {
    if (!selectedConvo) return;
    try { await chatAPI.muteConversation(selectedConvo.id); fetchConversations(); } catch (_) {}
    setConvoMenuAnchor(null);
  };
  const handleDeleteConvo = async () => {
    if (!selectedConvo) return;
    try { await chatAPI.deleteConversation(selectedConvo.id); setSelectedConvo(null); fetchConversations(); } catch (_) {}
    setConvoMenuAnchor(null);
  };

  useEffect(() => {
    if (newChatOpen) {
      chatAPI.getChattableUsers({ search: userSearch }).then(r => setChattableUsers(r.data)).catch(() => {});
    }
  }, [newChatOpen, userSearch]);

  const startConversation = async (userId) => {
    try {
      const res = await chatAPI.createConversation({ participant_id: userId });
      setNewChatOpen(false);
      setSelectedConvo(res.data);
      fetchConversations();
      fetchMessages(res.data.id);
    } catch (err) { setError(err.response?.data?.detail || 'Failed to start conversation'); }
  };

  const getOther = (convo) => {
    if (!convo) return {};
    const isOne = convo.participant_one_id === user?.id;
    return {
      name: isOne ? convo.participant_two_name : convo.participant_one_name,
      role: isOne ? convo.participant_two_role : convo.participant_one_role,
      avatar: isOne ? convo.participant_two_avatar : convo.participant_one_avatar,
      is_online: convo.is_online || false,
      last_seen: convo.last_seen,
    };
  };

  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return '';
    try { return 'last seen ' + formatDistanceToNow(new Date(lastSeen), { addSuffix: true }); }
    catch { return ''; }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  return (
    <Box sx={{ height: 'calc(100vh - 140px)', display: 'flex', flexDirection: 'column' }}>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}

      <Box display="flex" gap={0} sx={{ flex: 1, minHeight: 0, border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
        {/* ── Sidebar ── */}
        <Box sx={{ width: 360, minWidth: 300, borderRight: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', bgcolor: '#fff' }}>
          {/* Sidebar header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1.1rem' }}>Chats</Typography>
            <Box>
              <IconButton size="small" onClick={() => setSearchOpen(!searchOpen)}><Search fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => setNewChatOpen(true)} color="primary"><Add fontSize="small" /></IconButton>
            </Box>
          </Box>
          {searchOpen && (
            <Box px={1.5} py={1} bgcolor="#f0f2f5">
              <TextField fullWidth size="small" placeholder="Search messages..." value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: '#fff' } }}
              />
            </Box>
          )}
          {/* Conversation list */}
          <List disablePadding sx={{ flex: 1, overflow: 'auto' }}>
            {conversations.length === 0 ? (
              <Box p={4} textAlign="center">
                <ChatIcon sx={{ fontSize: 56, color: '#bbb', mb: 1 }} />
                <Typography color="text.secondary" variant="body2">No conversations yet</Typography>
                <Button size="small" onClick={() => setNewChatOpen(true)} sx={{ mt: 1 }}>Start a chat</Button>
              </Box>
            ) : (
              conversations.map((convo) => {
                const other = getOther(convo);
                const isSelected = selectedConvo?.id === convo.id;
                return (
                  <React.Fragment key={convo.id}>
                    <ListItem button selected={isSelected} onClick={() => setSelectedConvo(convo)}
                      sx={{ py: 1.2, px: 2, bgcolor: isSelected ? '#f0f2f5' : 'transparent', '&:hover': { bgcolor: '#f5f5f5' } }}>
                      <ListItemAvatar>
                        <Badge badgeContent={convo.unread_count} color="error" overlap="circular"
                          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                          <Badge variant="dot" overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            sx={{ '& .MuiBadge-dot': { bgcolor: other.is_online ? '#44b700' : '#bbb', width: 10, height: 10, borderRadius: '50%', border: '2px solid #fff' } }}>
                            <Avatar src={other.avatar ? `${API_BASE}${other.avatar}` : undefined}
                              sx={{ bgcolor: roleColors[other.role] || '#757575', width: 48, height: 48 }}>
                              {other.name?.[0] || '?'}
                            </Avatar>
                          </Badge>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText sx={{ ml: 0.5 }}
                        primary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                              {convo.is_pinned && <PushPin sx={{ fontSize: 14, color: '#999' }} />}
                              <Typography variant="subtitle2" noWrap fontWeight={convo.unread_count ? 700 : 500}>{other.name}</Typography>
                            </Box>
                            <Typography variant="caption" color={convo.unread_count ? 'primary' : 'text.secondary'} sx={{ flexShrink: 0, ml: 1 }}>
                              {formatConvoTime(convo.last_message_at)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: convo.unread_count ? 600 : 400, maxWidth: 180 }}>
                              {convo.is_muted && '🔇 '}{convo.last_message || 'No messages yet'}
                            </Typography>
                            <Chip label={other.role?.replace('_', ' ')} size="small"
                              sx={{ fontSize: '0.6rem', height: 16, ml: 0.5, bgcolor: roleColors[other.role], color: '#fff', textTransform: 'capitalize' }} />
                          </Box>
                        }
                      />
                    </ListItem>
                    <Divider sx={{ ml: 9 }} />
                  </React.Fragment>
                );
              })
            )}
          </List>
        </Box>

        {/* ── Message Area ── */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: '#efeae2', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'a\' patternUnits=\'userSpaceOnUse\' width=\'40\' height=\'40\'%3E%3Ccircle cx=\'20\' cy=\'20\' r=\'1\' fill=\'%23d4ccba\' opacity=\'.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect fill=\'url(%23a)\' width=\'200\' height=\'200\'/%3E%3C/svg%3E")' }}>
          {!selectedConvo ? (
            <Box display="flex" alignItems="center" justifyContent="center" flex={1} bgcolor="#f0f2f5">
              <Box textAlign="center">
                <Box sx={{ width: 220, height: 220, borderRadius: '50%', bgcolor: '#00a884', mx: 'auto', mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChatIcon sx={{ fontSize: 100, color: '#fff' }} />
                </Box>
                <Typography variant="h5" color="text.secondary" fontWeight={300}>CampusNest Chat</Typography>
                <Typography variant="body2" color="text.secondary" mt={1}>Send and receive messages with landlords, agents, and tenants</Typography>
              </Box>
            </Box>
          ) : (
            <>
              {/* Chat Header */}
              <Box sx={{ px: 2, py: 1, bgcolor: '#f0f2f5', borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Badge variant="dot" overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    sx={{ '& .MuiBadge-dot': { bgcolor: getOther(selectedConvo).is_online ? '#44b700' : '#bbb', width: 10, height: 10, borderRadius: '50%', border: '2px solid #f0f2f5' } }}>
                    <Avatar src={getOther(selectedConvo).avatar ? `${API_BASE}${getOther(selectedConvo).avatar}` : undefined}
                      sx={{ width: 40, height: 40, bgcolor: roleColors[getOther(selectedConvo).role] }}>
                      {getOther(selectedConvo).name?.[0]}
                    </Avatar>
                  </Badge>
                  <Box>
                    <Typography fontWeight={600} fontSize="0.95rem">{getOther(selectedConvo).name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typingUsers.length > 0
                        ? <em style={{ color: '#00a884' }}>typing...</em>
                        : getOther(selectedConvo).is_online
                          ? <span style={{ color: '#44b700' }}>online</span>
                          : getOther(selectedConvo).last_seen
                            ? formatLastSeen(getOther(selectedConvo).last_seen)
                            : getOther(selectedConvo).role?.replace('_', ' ')
                      }
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Tooltip title="Voice Call">
                    <IconButton size="small" sx={{ color: '#54656f' }}><Call fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Video Call">
                    <IconButton size="small" sx={{ color: '#54656f' }}><Videocam fontSize="small" /></IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={(e) => setConvoMenuAnchor(e.currentTarget)}><MoreVert fontSize="small" /></IconButton>
                </Box>
              </Box>
              {uploading && <LinearProgress sx={{ height: 2 }} />}

              {/* Messages */}
              <Box flex={1} overflow="auto" px={2} py={1} display="flex" flexDirection="column" gap={0.3}>
                {msgLoading && messages.length === 0 ? (
                  <Box display="flex" justifyContent="center" p={4}><CircularProgress size={28} sx={{ color: '#00a884' }} /></Box>
                ) : (
                  messages.map((msg, idx) => {
                    const isOwn = msg.sender_id === user?.id;
                    const showDate = idx === 0 || format(new Date(messages[idx - 1].created_at), 'yyyy-MM-dd') !== format(new Date(msg.created_at), 'yyyy-MM-dd');
                    return (
                      <React.Fragment key={msg.id}>
                        {showDate && (
                          <Box textAlign="center" my={1}>
                            <Chip label={isToday(new Date(msg.created_at)) ? 'Today' : isYesterday(new Date(msg.created_at)) ? 'Yesterday' : format(new Date(msg.created_at), 'dd MMM yyyy')}
                              size="small" sx={{ bgcolor: '#e1f3fb', fontWeight: 500, fontSize: '0.7rem' }} />
                          </Box>
                        )}
                        <Box alignSelf={isOwn ? 'flex-end' : 'flex-start'} maxWidth="65%" sx={{ position: 'relative', mb: 0.3 }}
                          onContextMenu={(e) => { e.preventDefault(); setContextMsg(msg); setContextMenu({ mouseX: e.clientX, mouseY: e.clientY }); }}>
                          {/* Reply preview */}
                          {msg.reply_to && (
                            <Box sx={{ bgcolor: isOwn ? '#c5e1a5' : '#e0e0e0', borderRadius: '8px 8px 0 0', px: 1.5, py: 0.5, borderLeft: '3px solid #00a884', fontSize: '0.75rem' }}>
                              <Typography variant="caption" fontWeight={600} color="#00a884">{msg.reply_to.sender_name}</Typography>
                              <Typography variant="caption" display="block" noWrap color="text.secondary">{msg.reply_to.content}</Typography>
                            </Box>
                          )}
                          <Paper elevation={0} sx={{
                            px: 1.5, py: 0.8, position: 'relative',
                            bgcolor: isOwn ? '#d9fdd3' : '#fff',
                            borderRadius: msg.reply_to ? '0 0 8px 8px' : '8px',
                            '&:hover .msg-actions': { opacity: 1 },
                          }}>
                            {/* Hover actions */}
                            <Box className="msg-actions" sx={{ position: 'absolute', top: 2, right: 4, opacity: 0, transition: 'opacity 0.2s', display: 'flex', gap: 0.2 }}>
                              <IconButton size="small" sx={{ p: 0.3 }} onClick={() => setReplyTo(msg)}><Reply sx={{ fontSize: 16 }} /></IconButton>
                              <IconButton size="small" sx={{ p: 0.3 }} onClick={(e) => { setEmojiAnchor(e.currentTarget); setEmojiMsg(msg); }}><EmojiEmotions sx={{ fontSize: 16 }} /></IconButton>
                            </Box>
                            {msg.is_deleted ? (
                              <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#999', fontSize: '0.85rem' }}>This message was deleted</Typography>
                            ) : (
                              <>
                                {msg.attachment_url && msg.message_type === 'image' && (
                                  <Box component="img" src={`${API_BASE}${msg.attachment_url}`} alt="" sx={{ maxWidth: '100%', borderRadius: 1, mb: 0.5, maxHeight: 300, cursor: 'pointer' }}
                                    onClick={() => window.open(`${API_BASE}${msg.attachment_url}`, '_blank')} />
                                )}
                                {msg.attachment_url && msg.message_type === 'audio' && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <audio controls src={`${API_BASE}${msg.attachment_url}`} style={{ width: '100%', maxWidth: 280, height: 36 }} />
                                  </Box>
                                )}
                                {msg.attachment_url && msg.message_type === 'video' && (
                                  <Box sx={{ mb: 0.5 }}>
                                    <video controls src={`${API_BASE}${msg.attachment_url}`} style={{ maxWidth: '100%', maxHeight: 280, borderRadius: 4 }} />
                                  </Box>
                                )}
                                {msg.attachment_url && msg.message_type === 'file' && (
                                  <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: isOwn ? '#c5e1a5' : '#f5f5f5', p: 1, borderRadius: 1, mb: 0.5, cursor: 'pointer' }}
                                    onClick={() => window.open(`${API_BASE}${msg.attachment_url}`, '_blank')}>
                                    <InsertDriveFile fontSize="small" />
                                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>{msg.attachment_name || 'File'}</Typography>
                                    <Download fontSize="small" sx={{ color: '#666' }} />
                                  </Box>
                                )}
                                {msg.content && <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</Typography>}
                              </>
                            )}
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={0.3} mt={0.2}>
                              {msg.is_edited && <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#999' }}>edited</Typography>}
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: '#999' }}>{format(new Date(msg.created_at), 'HH:mm')}</Typography>
                              {isOwn && (msg.is_read || msg.status === 'read' ?
                                <DoneAll sx={{ fontSize: 14, color: '#53bdeb' }} /> :
                                msg.status === 'delivered' ? <DoneAll sx={{ fontSize: 14, color: '#999' }} /> :
                                <Done sx={{ fontSize: 14, color: '#999' }} />
                              )}
                            </Box>
                            {/* Reactions */}
                            {msg.reactions && Object.keys(msg.reactions).length > 0 && (
                              <Box display="flex" gap={0.3} flexWrap="wrap" mt={0.3}>
                                {Object.entries(msg.reactions).map(([emoji, users]) => (
                                  <Chip key={emoji} label={`${emoji} ${users.length}`} size="small"
                                    onClick={() => handleReact(msg.id, emoji)}
                                    sx={{ height: 22, fontSize: '0.7rem', bgcolor: users.includes(user?.id) ? '#d9fdd3' : '#f0f0f0', cursor: 'pointer' }} />
                                ))}
                              </Box>
                            )}
                          </Paper>
                        </Box>
                      </React.Fragment>
                    );
                  })
                )}
                {typingUsers.length > 0 && (
                  <Box alignSelf="flex-start" maxWidth="30%">
                    <Paper elevation={0} sx={{ px: 2, py: 1, bgcolor: '#fff', borderRadius: 2 }}>
                      <Box display="flex" gap={0.5}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#999', animation: 'bounce 1.4s infinite ease-in-out', animationDelay: '0s' }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#999', animation: 'bounce 1.4s infinite ease-in-out', animationDelay: '0.2s' }} />
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#999', animation: 'bounce 1.4s infinite ease-in-out', animationDelay: '0.4s' }} />
                      </Box>
                    </Paper>
                  </Box>
                )}
                <div ref={messagesEndRef} />
              </Box>

              {/* Reply bar */}
              {replyTo && (
                <Box sx={{ px: 2, py: 0.8, bgcolor: '#f0f2f5', borderLeft: '4px solid #00a884', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box minWidth={0}>
                    <Typography variant="caption" fontWeight={600} color="#00a884">{replyTo.sender_name}</Typography>
                    <Typography variant="caption" display="block" noWrap color="text.secondary">{replyTo.content}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => setReplyTo(null)}><Close fontSize="small" /></IconButton>
                </Box>
              )}
              {editingMsg && (
                <Box sx={{ px: 2, py: 0.8, bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box minWidth={0}>
                    <Typography variant="caption" fontWeight={600} color="#ff9800">Editing message</Typography>
                    <Typography variant="caption" display="block" noWrap color="text.secondary">{editingMsg.content}</Typography>
                  </Box>
                  <IconButton size="small" onClick={() => { setEditingMsg(null); setNewMessage(''); }}><Close fontSize="small" /></IconButton>
                </Box>
              )}

              {/* Input */}
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#f0f2f5', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Emoji">
                  <IconButton size="small" onClick={(e) => setEmojiInputAnchor(e.currentTarget)} sx={{ color: '#54656f' }}>
                    <EmojiEmotions fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Attach">
                  <IconButton size="small" onClick={(e) => setAttachMenuAnchor(e.currentTarget)} sx={{ color: '#54656f' }} disabled={uploading}>
                    <AttachFile fontSize="small" />
                  </IconButton>
                </Tooltip>
                <TextField fullWidth size="small" placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => { setNewMessage(e.target.value); handleTyping(); }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  multiline maxRows={4}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 20, bgcolor: '#fff' } }}
                />
                <IconButton color="primary" onClick={handleSend} disabled={!newMessage.trim() || uploading}
                  sx={{ bgcolor: '#00a884', color: '#fff', '&:hover': { bgcolor: '#008f72' }, '&.Mui-disabled': { bgcolor: '#ccc' } }}>
                  <Send fontSize="small" />
                </IconButton>
              </Box>
              {/* Hidden file inputs */}
              <input type="file" hidden ref={fileInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar" onChange={(e) => handleFileUpload(e, 'file')} />
              <input type="file" hidden ref={imageInputRef} accept="image/*" onChange={(e) => handleFileUpload(e, 'image')} />
              <input type="file" hidden ref={audioInputRef} accept="audio/*" onChange={(e) => handleFileUpload(e, 'audio')} />
              <input type="file" hidden ref={videoInputRef} accept="video/*" onChange={(e) => handleFileUpload(e, 'video')} />
            </>
          )}
        </Box>
      </Box>

      {/* Context Menu */}
      <Menu open={!!contextMenu} onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition" anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <MenuItem onClick={() => { setReplyTo(contextMsg); setContextMenu(null); }}><Reply fontSize="small" sx={{ mr: 1 }} /> Reply</MenuItem>
        {contextMsg?.sender_id === user?.id && !contextMsg?.is_deleted && (
          <MenuItem onClick={() => { setEditingMsg(contextMsg); setNewMessage(contextMsg.content); setContextMenu(null); }}><Edit fontSize="small" sx={{ mr: 1 }} /> Edit</MenuItem>
        )}
        {contextMsg?.sender_id === user?.id && !contextMsg?.is_deleted && (
          <MenuItem onClick={() => handleDeleteMsg(contextMsg.id)}><Delete fontSize="small" sx={{ mr: 1 }} /> Delete</MenuItem>
        )}
      </Menu>

      {/* Emoji Popover */}
      <Popover open={!!emojiAnchor} anchorEl={emojiAnchor} onClose={() => { setEmojiAnchor(null); setEmojiMsg(null); }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }} transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Box display="flex" gap={0.5} p={1}>
          {QUICK_EMOJIS.map(e => (
            <IconButton key={e} size="small" onClick={() => handleReact(emojiMsg?.id, e)} sx={{ fontSize: 20 }}>{e}</IconButton>
          ))}
        </Box>
      </Popover>

      {/* Convo Menu */}
      <Menu anchorEl={convoMenuAnchor} open={!!convoMenuAnchor} onClose={() => setConvoMenuAnchor(null)}>
        <MenuItem onClick={handlePinConvo}><PushPin fontSize="small" sx={{ mr: 1 }} /> {selectedConvo?.is_pinned ? 'Unpin' : 'Pin'}</MenuItem>
        <MenuItem onClick={handleMuteConvo}><VolumeOff fontSize="small" sx={{ mr: 1 }} /> {selectedConvo?.is_muted ? 'Unmute' : 'Mute'}</MenuItem>
        <MenuItem onClick={handleDeleteConvo} sx={{ color: 'error.main' }}><Delete fontSize="small" sx={{ mr: 1 }} /> Delete Chat</MenuItem>
      </Menu>

      {/* Attachment Menu */}
      <Menu anchorEl={attachMenuAnchor} open={!!attachMenuAnchor} onClose={() => setAttachMenuAnchor(null)}>
        <MenuItem onClick={() => { imageInputRef.current?.click(); setAttachMenuAnchor(null); }}>
          <ImageIcon fontSize="small" sx={{ mr: 1, color: '#7c4dff' }} /> Photo
        </MenuItem>
        <MenuItem onClick={() => { videoInputRef.current?.click(); setAttachMenuAnchor(null); }}>
          <Videocam fontSize="small" sx={{ mr: 1, color: '#ff1744' }} /> Video
        </MenuItem>
        <MenuItem onClick={() => { audioInputRef.current?.click(); setAttachMenuAnchor(null); }}>
          <AudioFile fontSize="small" sx={{ mr: 1, color: '#ff9100' }} /> Audio
        </MenuItem>
        <MenuItem onClick={() => { fileInputRef.current?.click(); setAttachMenuAnchor(null); }}>
          <InsertDriveFile fontSize="small" sx={{ mr: 1, color: '#1976d2' }} /> Document
        </MenuItem>
      </Menu>

      {/* Emoji Input Picker */}
      <Popover open={!!emojiInputAnchor} anchorEl={emojiInputAnchor}
        onClose={() => setEmojiInputAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ width: 320, maxHeight: 260, overflow: 'auto', p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.2 }}>
          {EMOJI_PICKER.map((em, i) => (
            <IconButton key={i} size="small" onClick={() => { setNewMessage(prev => prev + em); setEmojiInputAnchor(null); }}
              sx={{ fontSize: 22, width: 36, height: 36 }}>{em}</IconButton>
          ))}
        </Box>
      </Popover>

      {/* New Chat Dialog */}
      <Dialog open={newChatOpen} onClose={() => setNewChatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#00a884', color: '#fff' }}>New Chat</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <TextField fullWidth size="small" placeholder="Search users..." value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)} sx={{ mb: 2 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
          <List>
            {chattableUsers.map((u) => (
              <ListItem key={u.id} button onClick={() => startConversation(u.id)} sx={{ borderRadius: 2, mb: 0.5 }}>
                <ListItemAvatar>
                  <Avatar src={u.avatar_url ? `${API_BASE}${u.avatar_url}` : undefined} sx={{ bgcolor: roleColors[u.role] || '#757575' }}>{u.first_name?.[0]}</Avatar>
                </ListItemAvatar>
                <ListItemText primary={`${u.first_name} ${u.last_name}`}
                  secondary={<Chip label={u.role?.replace('_', ' ')} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: roleColors[u.role], color: '#fff', textTransform: 'capitalize' }} />}
                />
              </ListItem>
            ))}
            {chattableUsers.length === 0 && <Typography color="text.secondary" textAlign="center" py={2}>No users available</Typography>}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setNewChatOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% { transform: scale(0); }
          40% { transform: scale(1); }
        }
      `}</style>
    </Box>
  );
}

export default ChatPage;
